import { useEffect, useState } from "react";
import moment from "moment";
import {
  findAllExchangeRates,
  fetchLiveExchangeRate,
  createExchangeRate,
} from "../api/currency";
import handleErrorMessage from "../errors/handleErrorMessage";
import { fetchAllPages } from "../api/pagination";
import { rateEffectiveDateKey } from "../rateEffectiveDate";

/**
 * Resolves which exchange rate applies to a given date for a given currency.
 * If no exchange rate is registered for the selected date, it automatically
 * fetches the official rate (live or historical) and registers it in the
 * background for that date while keeping a loading state.
 *
 * @param {object} params
 * @param {number|string|null} params.currencyId - id of the selected currency, or null/undefined if none selected yet.
 * @param {boolean} params.isUsdCurrency - true when the selected currency is the base currency (no conversion needed, rate is always 1).
 * @param {import('moment').Moment|null} params.date - the selected date (already parsed), or null if not chosen yet.
 * @param {number|null} params.activeExchangeRate - last-resort fallback rate (e.g. the condominium's currently active rate).
 * @param {number|string|null} params.condominiumId - optional condominium ID.
 */
const useExchangeRateForDate = ({
  currencyId,
  isUsdCurrency,
  date,
  activeExchangeRate,
  condominiumId,
}) => {
  const [rateByDate, setRateByDate] = useState(null);
  const [rateResolution, setRateResolution] = useState({
    isFallback: false,
    selectedDateKey: null,
    usedDateKey: null,
  });
  const [isRateLoading, setIsRateLoading] = useState(false);
  const [rateRefreshTick, setRateRefreshTick] = useState(0);

  useEffect(() => {
    if (!currencyId) {
      setRateResolution({ isFallback: false, selectedDateKey: null, usedDateKey: null });
      setRateByDate(activeExchangeRate || null);
      setIsRateLoading(false);
      return;
    }

    if (isUsdCurrency) {
      setRateResolution({ isFallback: false, selectedDateKey: null, usedDateKey: null });
      setRateByDate(1);
      setIsRateLoading(false);
      return;
    }

    if (!date) {
      setRateResolution({ isFallback: false, selectedDateKey: null, usedDateKey: null });
      setRateByDate(activeExchangeRate || null);
      setIsRateLoading(false);
      return;
    }

    let isMounted = true;

    const loadRateByDate = async () => {
      setIsRateLoading(true);
      const selectedDateKey = date.format("YYYY-MM-DD");
      // HO-TASK-292: un sábado o un domingo van a la tasa del lunes
      // siguiente, así que es esa la que se busca y, si falta, la que se
      // registra. Sin esto el formulario volvería a crear tasas de fin de
      // semana, que es justo lo que el backend dejó de hacer.
      const appliedDateKey = rateEffectiveDateKey(selectedDateKey) || selectedDateKey;
      if (isMounted) {
        setRateResolution({
          isFallback: false,
          selectedDateKey,
          usedDateKey: appliedDateKey,
        });
      }

      try {
        const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
          currency: currencyId,
        }));

        const rates = response?.data || [];
        const rateForDay = rates
          .filter((rate) => moment(rate.date || rate.created_at).format("YYYY-MM-DD") === appliedDateKey)
          .sort((a, b) => moment(b.date || b.created_at).valueOf() - moment(a.date || a.created_at).valueOf())[0];
        const latestRegisteredRate = rates
          .filter((rate) => Number(rate?.value || 0) > 0)
          .sort((a, b) => moment(b.date || b.created_at).valueOf() - moment(a.date || a.created_at).valueOf())[0];

        const resolvedRate = Number(rateForDay?.value || 0);

        if (resolvedRate > 0) {
          if (isMounted) {
            setRateResolution({
              isFallback: false,
              selectedDateKey,
              usedDateKey: moment(rateForDay.date || rateForDay.created_at).format("YYYY-MM-DD"),
            });
            setRateByDate(resolvedRate);
          }
        } else {
          // No registered rate for selected date: fetch live/historical & save in background
          let autoFetchedValue = null;
          let autoFetchedDateKey = appliedDateKey;
          try {
            const liveRes = await fetchLiveExchangeRate({
              condominium_id: condominiumId,
              date: selectedDateKey,
            });
            if (liveRes && Number(liveRes?.value) > 0) {
              autoFetchedValue = Number(liveRes.value);
              // El backend resuelve a qué día corresponde la tasa que
              // devuelve y lo dice en effective_date. Manda ese, no el
              // que se pidió.
              autoFetchedDateKey = liveRes.effective_date || appliedDateKey;
            }
          } catch (liveErr) {
            console.warn("No se pudo obtener la tasa oficial para la fecha:", liveErr);
          }

          if (autoFetchedValue > 0) {
            try {
              await createExchangeRate({
                currency: currencyId,
                exchangeRate: {
                  value: autoFetchedValue,
                  date: autoFetchedDateKey,
                },
              });
            } catch (saveErr) {
              console.warn("No se pudo registrar la tasa obtenida en trasfondo:", saveErr);
            }

            if (isMounted) {
              setRateResolution({
                isFallback: false,
                selectedDateKey,
                usedDateKey: autoFetchedDateKey,
              });
              setRateByDate(autoFetchedValue);
            }
          } else if (latestRegisteredRate) {
            if (isMounted) {
              const usedDateKey = moment(
                latestRegisteredRate.date || latestRegisteredRate.created_at
              ).format("YYYY-MM-DD");

              setRateResolution({
                isFallback: true,
                selectedDateKey,
                usedDateKey,
              });
              setRateByDate(Number(latestRegisteredRate.value));
            }
          } else {
            if (isMounted) {
              setRateResolution({
                isFallback: true,
                selectedDateKey,
                usedDateKey: null,
              });
              setRateByDate(activeExchangeRate || null);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setRateByDate(activeExchangeRate || null);
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setIsRateLoading(false);
        }
      }
    };

    loadRateByDate();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeExchangeRate, isUsdCurrency, date, rateRefreshTick, currencyId, condominiumId]);

  const isRateRegistrationRequired = !isUsdCurrency && Boolean(rateResolution?.isFallback);

  const appliedExchangeRate = isUsdCurrency ? 1 : Number(rateByDate ?? activeExchangeRate ?? 0);

  return {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
    isRateRegistrationRequired,
    rateRefreshTick,
    bumpRateRefreshTick: () => setRateRefreshTick((prev) => prev + 1),
  };
};

export default useExchangeRateForDate;
